// Colorisation du facteur de puissance (cos φ) — vert / orange / rouge.
//
// Seuils calés sur la facturation française, pas sur la théorie : ce qui
// compte pour le client, c'est le point à partir duquel le réactif se paie.
// L'energie reactive est facturee au-dela de tan φ = 0,4, soit
//   cos φ = 1 / √(1 + 0,4²) ≈ 0,928  →  arrondi a 0,93
//
//   ≥ 0,93    aucune penalite de reactif
//   0,85–0,93 seuil de facturation depasse, le reactif commence a couter
//   < 0,85    installation fortement inductive, compensation recommandee
//             (batterie de condensateurs)
//
// Les trois constantes ci-dessous sont le seul point de réglage : tout le
// reste du fichier et l'affichage s'y adaptent automatiquement.

import { translate } from '../i18n';
import type { TranslationKey } from '../i18n/translations';

/** cos φ ≥ ce seuil → vert (tan φ ≤ 0,4 : aucune pénalité de réactif). */
export const PF_GOOD = 0.93;

/** cos φ ≥ ce seuil → orange. En dessous → rouge. */
export const PF_WARNING = 0.85;

/**
 * Puissance active minimale (W) en dessous de laquelle le cos φ n'est pas
 * mesurable. À 0 W il n'y a aucun courant à déphaser : le compteur retourne
 * 1,000 par défaut, une valeur qu'il ne faut surtout pas afficher en vert.
 *
 * Fixé bas (5 W) car les WattelEM supervisent des circuits fins : dès qu'un
 * courant est réellement mesuré, on colorise.
 */
export const PF_MIN_LOAD_W = 5;

export type PowerFactorLevel = 'good' | 'warning' | 'critical' | 'idle' | 'unknown';

export interface PowerFactorStatus {
  level: PowerFactorLevel;
  /** Libellé court affiché sous la valeur. Vide si rien à signaler. */
  label: string;
  /** Classe Tailwind de la valeur. */
  text: string;
  /** Classe Tailwind du fond de la tuile. */
  bg: string;
  /** Classes Tailwind du badge. */
  badge: string;
  /** Classe Tailwind de la pastille du badge. */
  dot: string;
  /** Explication affichée au survol. */
  hint: string;
}

// Classes Tailwind écrites en toutes lettres : le compilateur JIT ne détecte
// pas les noms construits dynamiquement.
// Libellés et explications : clés i18n, résolues au moment de l'appel.
type StatusDef = Omit<PowerFactorStatus, 'level' | 'label' | 'hint'> & {
  label: TranslationKey | null;
  hint: TranslationKey;
};

const STATUS: Record<PowerFactorLevel, StatusDef> = {
  good: {
    label: 'eq_pf_good',
    text: 'text-emerald-600',
    bg: 'bg-emerald-50',
    badge: 'bg-emerald-100 text-emerald-700',
    dot: 'bg-emerald-500',
    hint: 'eq_pf_hintGood',
  },
  warning: {
    label: 'eq_pf_warning',
    text: 'text-amber-600',
    bg: 'bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
    hint: 'eq_pf_hintWarning',
  },
  critical: {
    label: 'eq_pf_critical',
    text: 'text-rose-600',
    bg: 'bg-rose-50',
    badge: 'bg-rose-100 text-rose-700',
    dot: 'bg-rose-500',
    hint: 'eq_pf_hintCritical',
  },
  idle: {
    label: 'eq_pf_idle',
    text: 'text-zce-gray-muted',
    bg: 'bg-zce-gray-light',
    badge: 'bg-gray-200 text-gray-600',
    dot: 'bg-gray-400',
    hint: 'eq_pf_hintIdle',
  },
  unknown: {
    label: null,
    text: 'text-purple-600',
    bg: 'bg-purple-50',
    badge: '',
    dot: '',
    hint: 'eq_pf_hintUnknown',
  },
};

type Translator = (key: TranslationKey, vars?: Record<string, string | number>) => string;

function build(level: PowerFactorLevel, tr: Translator): PowerFactorStatus {
  const { label, hint, ...classes } = STATUS[level];
  return {
    level,
    ...classes,
    label: label ? tr(label) : '',
    hint: tr(hint, { good: PF_GOOD, warning: PF_WARNING, min: PF_MIN_LOAD_W }),
  };
}

/**
 * Classe un cos φ selon les seuils ci-dessus.
 *
 * @param powerFactor cos φ mesuré, ou null si non remonté.
 * @param powerW      puissance active en watts, pour écarter la mesure à vide.
 * @param tr          fonction de traduction (t de useI18n dans un composant) ;
 *                    à défaut, translate() hors composant.
 */
export function classifyPowerFactor(
  powerFactor: number | null | undefined,
  powerW: number | null | undefined,
  tr: Translator = translate,
): PowerFactorStatus {
  if (powerFactor === null || powerFactor === undefined || Number.isNaN(powerFactor)) {
    return build('unknown', tr);
  }
  // Sous charge négligeable, un cos φ bas est normal : on n'alerte pas.
  if (powerW !== null && powerW !== undefined && powerW < PF_MIN_LOAD_W) {
    return build('idle', tr);
  }
  // Le cos φ peut être signé (sens du flux) : seule l'amplitude nous intéresse.
  const pf = Math.abs(powerFactor);
  if (pf >= PF_GOOD) return build('good', tr);
  if (pf >= PF_WARNING) return build('warning', tr);
  return build('critical', tr);
}

export default classifyPowerFactor;
